// @ts-nocheck
import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Eye, EyeOff, Pin, PinOff, ArrowUpDown } from 'lucide-react';

interface CoinHeaderCellProps {
  title?: string;
}

interface StandardHeaderCellProps {
  colKey: string;
  label: string;
  abbrev: string;
  sortableIcon?: boolean;
  secondary?: React.ReactNode;
  visible: boolean;
  pinned: boolean;
  onToggleColumn: (colKey: string) => void;
  onTogglePin: (colKey: string) => void;
}

/**
 * CoinHeaderCell - For the first column (Coin) that's always pinned and can't be hidden
 * Used for coin/asset name columns that are always visible
 */
export const CoinHeaderCell: React.FC<CoinHeaderCellProps> = ({ title = "Coin" }) => {
  const { isDark } = useTheme();

  return (
    <div className="w-full h-full flex flex-col">
      {/* CONTROL STRIP — fixed height */}
      <div className={`w-full h-7 flex items-center justify-center px-1 ${
        isDark ? 'bg-zinc-800 text-white/70' : 'bg-gray-100 text-gray-600'
      }`}>
        {/* Empty space to maintain uniform layout */}
      </div>
      {/* TITLE AREA — fills remaining height */}
      <div className="w-full flex-1 flex items-center justify-center px-3">
        <span>{title}</span>
      </div>
    </div>
  );
};

/**
 * StandardHeaderCell - For all other columns with eye/pin/sort functionality
 * Has grey control strip with eye and pin buttons, and title area with sort icon
 */
export const StandardHeaderCell: React.FC<StandardHeaderCellProps> = ({
  colKey,
  label,
  abbrev,
  sortableIcon = true,
  secondary,
  visible,
  pinned,
  onToggleColumn,
  onTogglePin
}) => {
  const { isDark } = useTheme();

  // Fixed-height control strip (grey bar)
  const barCls = `${isDark ? 'bg-zinc-800 text-white/70' : 'bg-gray-100 text-gray-600'}`;

  return (
    <div className="w-full h-full flex flex-col">
      {/* CONTROL STRIP — fixed height */}
      <div className={`w-full h-7 flex items-center justify-between px-1 ${barCls}`}>
        <button
          onClick={(e) => { e.stopPropagation(); onToggleColumn(colKey); }}
          className={`p-0.5 rounded ${
            isDark ? 'hover:bg-zinc-700 hover:text-white' : 'hover:bg-gray-200 hover:text-gray-900'
          }`}
          title={visible ? 'Hide column' : 'Show column'}
        >
          {visible ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
        </button>

        {visible && (
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.stopPropagation();
              onTogglePin(colKey);
              (e.currentTarget as HTMLButtonElement).blur();
            }}
            className={`p-0.5 rounded ${
              isDark ? 'hover:bg-zinc-700 hover:text-white' : 'hover:bg-gray-200 hover:text-gray-900'
            }`}
            title="Pin column"
          >
            {pinned ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
          </button>
        )}
      </div>

      {/* TITLE AREA — fills remaining height */}
      <div className="w-full flex-1 flex items-center justify-center px-3">
        {visible ? (
          <div className="flex flex-col items-center gap-1">
            <div className="flex items-center gap-1">
              <span>{label}</span>
              {sortableIcon && <ArrowUpDown className="h-4 w-4 opacity-60" />}
            </div>
            {secondary && (
              <div className="opacity-70 text-xs">
                {secondary}
              </div>
            )}
          </div>
        ) : (
          <span className="text-xs">{abbrev}</span>
        )}
      </div>
    </div>
  );
};

/**
 * Utility function to get standard table header cell styling
 * Returns the className string for <th> elements
 */
export const getTableHeaderCellClasses = (
  visible: boolean,
  pinned: boolean,
  isDark: boolean
): string => {
  return `h-24 text-center align-top transition-all duration-300 ${
    visible ? '' : 'w-8 max-w-8'
  } ${pinned ? 'sticky' : ''}`;
};

/**
 * Utility function to get sticky positioning styles for pinned columns
 */
export const getStickyPositionStyle = (
  pinned: boolean,
  leftPosition: number,
  isDark: boolean
): React.CSSProperties | undefined => {
  if (!pinned) return undefined;

  return {
    position: 'sticky',
    left: leftPosition,
    top: 0,
    zIndex: 2,
    background: isDark ? '#09090b' : '#ffffff'
  };
};